import { config } from "api/apiRoutes";

import {
    EventRequest,
    FileUploadConfirmationRequest,
    FileUploadRequest,
    FileUploadResponse,
    ReOpenEventRequest,
} from "api/thorRequestModels/Events/EventRequest";

import { EventDetails } from "models/thorEventModels/EventDetails";
import { EventDeviceData } from "models/thorEventModels/DeviceData";
import { BatchRequest } from "api/thorRequestModels/Events/BatchRequest";
import { BatchStatusAuthRequest } from "api/thorRequestModels/Events/BatchStatusRequest";
import { BatchFlowRequest } from "api/thorRequestModels/Events/BatchFlowRequest";
import { torAxios } from "api/serviceConfig";
import { HistoricalEventRequest } from "api/thorRequestModels/Events/HistoricalEventRequest";
import { EventBatchDetails } from "models/thorEventModels/EventBatchDetails";
import { EventPriorityRequest } from "api/thorRequestModels/Events/EventPriorityRequest";
import { AssignUserRequest } from "api/thorRequestModels/Events/AssignUserRequest";
import { AssignAssessmentRequest } from "api/thorRequestModels/Events/AssignAssessmentRequest";

import { AxiosResponse } from "axios";

import { PageRequest } from "api/sharedModels/PageRequest";
import { ActiveEvent } from "models/thorEventModels/ActiveEvents";
import { Page } from "models/shared/Page";
import { ProductQuantityRequest } from "api/thorRequestModels/Events/ProductQuantityRequest";
import { ReturnToStockRequest } from "api/thorRequestModels/Events/ReturnToStockRequest";
import { EventTypeRequest } from "api/thorRequestModels/Events/EventTypeRequest";
import CustomerIdRequest from "api/sharedModels/CustomerIdRequest";
import { ChangeDueDateRequest } from "api/thorRequestModels/Events/ChangeDueDateRequest";

export class ThorService {
    async getActiveEvents(data: PageRequest) {
        return await torAxios.get<Page<ActiveEvent>>(config.activeEvents, {
            params: {
                customerId: data.customerId,
                pageNumber: data.pageNumber,
                pageSize: data.pageSize,
            },
        });
    }

    async getEventDetails(data: EventRequest) {
        return await torAxios.get<EventDetails>(config.eventDetails(data.eventId), {
            params: {
                customerId: data.customerId,
                eventId: data.eventId,
            },
        });
    }

    async getEventMonitorsData(data: EventRequest) {
        return await torAxios.get<EventDeviceData>(config.deviceData(data.eventId), {
            params: {
                customerId: data.customerId,
                eventId: data.eventId,
            },
        });
    }

    async getHistoricalEventDetails(data: HistoricalEventRequest) {
        return await torAxios.get<EventBatchDetails>(config.historicalEventDetails(data.eventId, data.batchId), {
            params: {
                customerId: data.customerId,
                eventId: data.eventId,
                batchId: data.batchId,
            },
        });
    }

    async setBatchStatus(data: BatchStatusAuthRequest) {
        return await torAxios.patch(config.eventBatchStatus, {
            customerId: data.customerId,
            batchId: data.batchId,
            eventId: data.eventId,
            isApproved: data.isApproved,
            approvalMessage: data.approvalMessage,
            authModel: data.authModel,
        });
    }

    async setEventStatus(data: EventRequest) {
        return await torAxios.patch(config.closeEvent(data.eventId), {
            customerId: data.customerId,
            eventId: data.eventId,
        });
    }

    async downloadEventPDF(data: EventRequest) {
        return await torAxios.get(config.downloadPDF(data.eventId), {
            responseType: "blob",
            params: {
                customerId: data.customerId,
                eventId: data.eventId,
            },
        });
    }

    async getProductsFlow(data: BatchRequest) {
        return await torAxios.get(config.flows(data.batchId), {
            params: {
                customerId: data.customerId,
                batchId: data.batchId,
            },
        });
    }

    async setProductFlow(data: BatchFlowRequest) {
        return await torAxios.patch(config.assignFlow, {
            customerId: data.customerId,
            batchId: data.batchId,
            flowId: data.flowId,
        });
    }

    async getEventSummary(data: EventRequest) {
        return await torAxios.get(config.eventSummary(data.eventId), {
            params: {
                customerId: data.customerId,
                eventId: data.eventId,
            },
        });
    }

    async setEventPriority(data: EventPriorityRequest) {
        return await torAxios.patch(config.eventPriority, {
            customerId: data.customerId,
            eventId: data.eventId,
            priority: data.priority,
        });
    }

    async assignUserToEvent(data: AssignUserRequest) {
        return await torAxios.patch(config.assignUserToEvent(data.eventId), {
            customerId: data.customerId,
            eventId: data.eventId,
            userId: data.userId,
            assignUserType: data.assignUserType,
        });
    }

    async setAssessmentStatus(data: AssignAssessmentRequest) {
        return await torAxios.patch(config.setAssessmentStatus, {
            customerId: data.customerId,
            eventId: data.eventId,
            batchId: data.batchId,
        });
    }

    getUploadPresignedUrl(data: FileUploadRequest, file: File): Promise<AxiosResponse<FileUploadResponse>> {
        return torAxios.get(config.getUploadPresignedUrl, {
            params: {
                customerId: data.customerId,
                eventId: data.eventId,
                fileName: data.fileName,
                contentType: data.contentType,
            },
        });
    }

    uploadToS3WithPresignedUrl(
        file: File,
        url: string,
        uploadProgressCallback: Function | any
    ): Promise<AxiosResponse> {
        // Authorization: "" to skip interceptor with authorization to avoid aws error;
        return torAxios.put(url, file, {
            headers: { "Content-Type": file.type, Authorization: "" },
            onUploadProgress: uploadProgressCallback,
        });
    }

    downloadFromS3WithPresignedUrl(url: string) {
        // Authorization: "" to skip interceptor with authorization to avoid aws error;
        return torAxios.get<Blob>(url, {
            responseType: "blob",
            headers: { Authorization: "" },
        });
    }

    async confirmUpload(data: FileUploadConfirmationRequest) {
        return await torAxios.post(config.confirmUpload, data);
    }

    async getDownloadPresignedUrl(attachmentId: number, customerId: number) {
        return await torAxios.get<FileUploadResponse>(config.getDownloadPresignedUrl, {
            params: {
                customerId: customerId,
                attachmentId: attachmentId,
            },
        });
    }

    async getFormDownloadPresignedUrl(formId: number, customerId: number) {
        return await torAxios.get<string>(config.getFormDownloadPresignedUrl, {
            params: {
                customerId: customerId,
                formId: formId,
            },
        });
    }

    async deleteEventAttachment(attachmentId: number, customerId: number) {
        return torAxios.delete(config.deleteEventAttachment, {
            params: {
                customerId: customerId,
                attachmentId: attachmentId,
            },
        });
    }

    async setProductQuantity(data: ProductQuantityRequest) {
        return await torAxios.patch(config.setQuantity, {
            customerId: data.customerId,
            eventId: data.eventId,
            transportationUnitId: data.transportationUnitId,
            quantity: data.quantity,
        });
    }

    async reopenEvent(data: ReOpenEventRequest) {
        return await torAxios.post(config.reopenEvent(data.eventId), {
            customerId: data.customerId,
            eventId: data.eventId,
        });
    }

    async returnToStock(data: ReturnToStockRequest) {
        return await torAxios.patch(config.returnToStock, {
            customerId: data.customerId,
            eventId: data.eventId,
            batchId: data.batchId,
            decision: data.decision,
        });
    }

    async setEventType(data: EventTypeRequest) {
        return await torAxios.patch(config.eventType, {
            customerId: data.customerId,
            eventId: data.eventId,
            eventType: data.eventType,
        });
    }

    async changeDueDate(data: ChangeDueDateRequest) {
        return await torAxios.patch(config.changeDueDate(data.eventId), {
            customerId: data.customerId,
            dueDate: data.dueDate,
        });
    }

    async resetAllEvents(data: CustomerIdRequest) {
        return await torAxios.post(config.resetAllEvents, {
            customerId: data.customerId,
        });
    }

    async resetEvent(data: EventRequest) {
        return await torAxios.post(config.resetEvent(data.eventId), {
            eventId: data.eventId,
            customerId: data.customerId,
        });
    }
}
